import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './RoleSelector.css';

export function RoleSelector({ onRoleSelected, userEmail, userId }) {
  const { t, i18n } = useTranslation();
  const [tracks, setTracks] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTracks();
  }, [userId]);

  const loadTracks = async () => {
    try {
      // Use userId from props first, fallback to localStorage
      const resolvedUserId = userId || localStorage.getItem('user_id') || localStorage.getItem('userId');
      console.log('[RoleSelector] userId from props:', userId);
      console.log('[RoleSelector] userId resolved:', resolvedUserId);

      let url = '/api/training/tracks';
      if (resolvedUserId) {
        url += `?userId=${encodeURIComponent(resolvedUserId)}`;
        console.log('[RoleSelector] Fetching tracks with userId:', resolvedUserId);
      } else {
        console.log('[RoleSelector] No userId found, fetching all tracks');
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to load tracks');
      const data = await response.json();
      const tracksList = Array.isArray(data) ? data : (data.tracks || data);
      console.log('[RoleSelector] Received tracks:', tracksList.map(t => t.id));
      setTracks(tracksList);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleSelectRole = async (role) => {
    setSelectedRole(role.role);
    try {
      await fetch('/api/training/user-profile', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ primaryRole: role.role })
      });
    } catch (err) {
      console.error('Failed to save track:', err);
    }
    onRoleSelected(role.role);
  };

  if (loading) {
    return (
      <div className="role-selector-container">
        <div className="role-selector-loading">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="role-selector-container">
        <div className="role-selector-error">{t('common.error')}: {error}</div>
      </div>
    );
  }

  return (
    <div className="role-selector-container">
      {/* Language Switcher */}
      <div className="language-switcher" style={{
        position: 'absolute',
        top: 20,
        right: 20,
        display: 'flex',
        gap: '8px'
      }}>
        {['en', 'pt', 'es'].map((lang) => (
          <button
            key={lang}
            onClick={() => i18n.changeLanguage(lang)}
            style={{
              padding: '6px 12px',
              border: `2px solid ${i18n.language === lang ? '#58A6FF' : '#30363D'}`,
              background: i18n.language === lang ? 'rgba(88, 166, 255, 0.1)' : 'transparent',
              borderRadius: '6px',
              color: i18n.language === lang ? '#58A6FF' : '#8B949E',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: i18n.language === lang ? '600' : '400',
              transition: 'all 0.2s'
            }}
          >
            {lang === 'en' ? '🇬🇧 EN' : lang === 'pt' ? '🇧🇷 PT' : '🇪🇸 ES'}
          </button>
        ))}
      </div>

      <div className="role-selector-header">
        <h1>{t('trackSelection.welcome')}</h1>
        <p>{t('trackSelection.subtitle')}</p>
      </div>

      <div className="role-selector-grid">
        {tracks.map((track) => {
          // Translate track name and description based on track.id
          const trackTransKey = `trackSelection.tracks.${track.id}`;
          const translatedName = t(`${trackTransKey}.name`, { defaultValue: track.name });
          const translatedDescription = t(`${trackTransKey}.description`, { defaultValue: track.description });

          return (
            <button
              key={track.id}
              onClick={() => handleSelectRole(track)}
              className={`role-card ${selectedRole === track.role ? 'selected' : ''}`}
              style={{ borderColor: track.color }}
            >
              <div className="role-icon">{track.icon}</div>
              <h2 className="role-name">{translatedName}</h2>
              <p className="role-description">{translatedDescription}</p>
              <div className="role-action">
                {selectedRole === track.role ? '✓ ' + t('common.confirm') : t('trackSelection.selectTrack')}
              </div>
            </button>
          );
        })}
      </div>

      <div className="role-selector-info">
        <p>{t('trackSelection.description')}</p>
      </div>
    </div>
  );
}

export default RoleSelector;
